import { useMemo } from 'react';

import { usePayorDetails } from 'app/api/hooks/core/v1/auth/user';
import { useGetProfileSettings } from 'app/api/hooks/core/v1/payor/reports';
import { PayorProfileType } from 'app/constants/user';
import { useAuthState } from 'app/state/AuthContext/hooks';
import { isInternalIntegrationsManager } from 'app/utils/lib/user';

interface UseGetPayorProfileSettingReturnType {
  boolean: boolean;
  number: number;
  string: string;
}

interface UseGetPayorProfileSettingOptions<T extends keyof UseGetPayorProfileSettingReturnType> {
  format?: T;
}

export const useGetPayorProfileSetting = <T extends keyof UseGetPayorProfileSettingReturnType = 'string'>(
  profileSettingId: PayorProfileType,
  options?: UseGetPayorProfileSettingOptions<T>,
): null | UseGetPayorProfileSettingReturnType[T] => {
  const { data } = useGetProfileSettings();

  const profileSetting = data?.[profileSettingId.toString()];

  if (!profileSetting) {
    return null;
  }

  switch (options?.format) {
    case 'boolean':
      return (profileSetting.value.toLowerCase() === true.toString()) as UseGetPayorProfileSettingReturnType[T];
    case 'number':
      return Number(profileSetting.value) as UseGetPayorProfileSettingReturnType[T];
    default:
      return profileSetting.value as UseGetPayorProfileSettingReturnType[T];
  }
};

export const useMayUploadFiles = () => {
  const { authRoles } = useAuthState();
  const payorDetails = usePayorDetails();

  const isPermittedManualUpload = useGetPayorProfileSetting(PayorProfileType.ManualUpload, { format: 'boolean' });

  return useMemo<boolean>(() =>
    isInternalIntegrationsManager(authRoles)
    && !!isPermittedManualUpload
    && !!payorDetails?.isOperationsNoBuy,
  [authRoles, isPermittedManualUpload, payorDetails?.isOperationsNoBuy]);
};
